import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import API_CONFIG from '../../config/api';

/**
 * Página pública — /portal-prestador/propostas/:token
 * Sem login e sem cadastro. Cadastro do prestador ocorre depois (Contratos).
 */
export default function PortalPrestadorPropostaPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [edital, setEdital] = useState(null);
  const [form, setForm] = useState({
    razaoSocial: '',
    cnpj: '',
    email: '',
    telefone: '',
    valor: '',
    descricao: '',
  });
  const [filesByKey, setFilesByKey] = useState({});

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setErro('');
    fetch(`${API_CONFIG.BACKEND_URL}/api/public/portal-prestador/propostas/${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'Link inválido');
        setEdital(data);
      })
      .catch((e) => setErro(e.message || 'Erro ao carregar edital'))
      .finally(() => setLoading(false));
  }, [token]);

  const docs = useMemo(
    () => (Array.isArray(edital?.documentos) ? edital.documentos : []),
    [edital]
  );

  const baixarTr = () => {
    window.open(
      `${API_CONFIG.BACKEND_URL}/api/public/portal-prestador/propostas/${encodeURIComponent(token)}/tr`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const enviar = async () => {
    setErro('');
    setOkMsg('');
    if (!form.razaoSocial.trim() || !form.cnpj.trim() || !form.email.trim()) {
      setErro('Preencha nome da empresa, CNPJ e e-mail.');
      return;
    }
    const obrigatorios = docs.filter((d) => d.obrigatorio !== false);
    const faltando = obrigatorios.filter((d) => !filesByKey[d.key]);
    if (faltando.length) {
      setErro(`Anexe os documentos obrigatórios: ${faltando.map((d) => d.label).join(', ')}`);
      return;
    }
    const anexos = docs
      .map((d) => ({ tipo: d.label || d.key, file: filesByKey[d.key] }))
      .filter((x) => x.file);
    if (!anexos.length) {
      setErro('Anexe ao menos a proposta comercial em PDF.');
      return;
    }

    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('razaoSocial', form.razaoSocial.trim());
      fd.append('cnpj', form.cnpj.trim());
      fd.append('email', form.email.trim());
      if (form.telefone) fd.append('telefone', form.telefone.trim());
      if (form.valor) fd.append('valor', form.valor);
      if (form.descricao) fd.append('descricao', form.descricao.trim());
      fd.append('tiposDocumento', JSON.stringify(anexos.map((a) => a.tipo)));
      anexos.forEach((a) => fd.append('arquivos', a.file));

      const res = await fetch(
        `${API_CONFIG.BACKEND_URL}/api/public/portal-prestador/propostas/${encodeURIComponent(token)}/proposta`,
        { method: 'POST', body: fd }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao enviar proposta');
      setOkMsg(data.message || 'Proposta enviada com sucesso.');
      setFilesByKey({});
    } catch (e) {
      setErro(e.message || 'Erro ao enviar');
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="#f5f9ff">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box minHeight="100vh" bgcolor="#f0f4f8" py={4}>
      <Container maxWidth="md">
        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Typography variant="overline" color="primary" fontWeight={800} letterSpacing={1}>
            Portal do Prestador — SGC
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, mb: 1 }}>
            {edital?.rotulo || 'Edital'}
          </Typography>

          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>Não é necessário cadastro nem login.</strong> Preencha o formulário, anexe os PDFs e
            envie. O cadastro no portal (se necessário) ocorre depois, pelo setor de Contratos.
          </Alert>

          {edital?.objeto ? (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              <strong>Objeto:</strong> {edital.objeto}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {edital?.dataEncerramento && (
              <Typography variant="body2" color="text.secondary">
                Prazo de envio até:{' '}
                <strong>{String(edital.dataEncerramento).replace('T', ' ').slice(0, 16)}</strong>
              </Typography>
            )}
            {edital?.statusPrazo && (
              <Typography variant="body2" color="text.secondary">
                · Status: <strong>{edital.statusPrazo}</strong>
              </Typography>
            )}
          </Stack>

          {erro ? <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert> : null}
          {okMsg ? <Alert severity="success" sx={{ mb: 2 }}>{okMsg}</Alert> : null}

          {!edital ? null : (
            <Stack spacing={2.5}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={baixarTr}
                sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}
              >
                Baixar Termo de Referência (PDF)
              </Button>

              {edital.clausulaTitulo && (
                <Alert severity="info">
                  <strong>{edital.clausulaTitulo}</strong>
                  {edital.textoClausula ? (
                    <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                      {String(edital.textoClausula).slice(0, 600)}
                      {String(edital.textoClausula).length > 600 ? '…' : ''}
                    </Typography>
                  ) : null}
                </Alert>
              )}

              <Divider />
              <Typography variant="h6" fontWeight={800}>
                Enviar proposta
              </Typography>

              {!edital.podeEnviar ? (
                <Alert severity="warning">
                  Este edital não está aberto para envio de propostas no momento.
                </Alert>
              ) : (
                <>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      label="Nome da empresa"
                      fullWidth
                      required
                      value={form.razaoSocial}
                      onChange={(e) => setForm((f) => ({ ...f, razaoSocial: e.target.value }))}
                    />
                    <TextField
                      label="CNPJ"
                      fullWidth
                      required
                      value={form.cnpj}
                      onChange={(e) => setForm((f) => ({ ...f, cnpj: e.target.value }))}
                    />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      label="E-mail"
                      fullWidth
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    />
                    <TextField
                      label="Telefone"
                      fullWidth
                      value={form.telefone}
                      onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))}
                    />
                  </Stack>
                  <TextField
                    label="Valor da proposta (R$)"
                    fullWidth
                    value={form.valor}
                    onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))}
                    helperText="Use vírgula para centavos"
                  />
                  <TextField
                    label="Observações"
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.descricao}
                    onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                  />

                  <Typography variant="subtitle2" fontWeight={800}>
                    Documentação exigida
                  </Typography>
                  {docs.map((d) => (
                    <Box
                      key={d.key}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px dashed',
                        borderColor: 'divider',
                        bgcolor: 'rgba(25,118,210,0.03)',
                      }}
                    >
                      <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                        {d.label}
                        {d.obrigatorio !== false ? ' *' : ' (opcional)'}
                      </Typography>
                      <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        {filesByKey[d.key] ? filesByKey[d.key].name : 'Selecionar PDF'}
                        <input
                          type="file"
                          hidden
                          accept=".pdf,application/pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setFilesByKey((prev) => ({ ...prev, [d.key]: file }));
                          }}
                        />
                      </Button>
                    </Box>
                  ))}

                  <Button
                    variant="contained"
                    size="large"
                    disabled={enviando || !!okMsg}
                    onClick={enviar}
                    startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : null}
                    sx={{ textTransform: 'none', fontWeight: 800, alignSelf: 'flex-start' }}
                  >
                    {enviando ? 'Enviando...' : 'Enviar proposta'}
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
